import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminEcuTuning = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, reviewed, paid, completed, rejected

  useEffect(() => {
    if (!isAuthenticated() || user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API_URL}/api/admin/ecu-tuning/requests`
        : `${API_URL}/api/admin/ecu-tuning/requests?status=${filter}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests);
      }
    } catch (error) {
      console.error('Error fetching ECU requests:', error);
      toast.error(t('common.errors.failed'), { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestDetails = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ecu-tuning/requests/${requestId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedRequest(data);
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error(t('common.errors.failed'), { autoClose: 1000 });
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ecu-tuning/requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`${t('admin.ecuTuning.details.updateStatus')}: ${newStatus}`, { autoClose: 1000 });
        fetchRequests();
        setSelectedRequest(null);
      } else {
        toast.error(t('common.errors.failed'), { autoClose: 1000 });
      }
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error(t('common.errors.failed'), { autoClose: 1000 });
    }
  };

  if (loading && !selectedRequest) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-racing-red border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('admin.ecuTuning.title')}</h1>
          <p className="text-gray-600">{t('admin.ecuTuning.subtitle')}</p>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-2">
          {['all', 'new', 'reviewed', 'paid', 'completed', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === status
                  ? 'bg-racing-red text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(`admin.ecuTuning.filters.${status}`)}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Requests List */}
          <div className="md:col-span-2">
            {requests.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">{t('admin.ecuTuning.list.none')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => fetchRequestDetails(request.id)}
                    className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition border-l-4 ${
                      selectedRequest?.id === request.id ? 'border-racing-red' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{request.full_name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{request.motorcycle}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded whitespace-nowrap ml-2 ${
                        request.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        request.status === 'reviewed' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                        request.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {t(`admin.ecuTuning.filters.${request.status}`)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>{request.email}</p>
                      <p>{request.phone}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(request.created_at).toLocaleString()}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Details Panel */}
          {selectedRequest && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-sm p-6 max-h-[calc(100vh-200px)] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold text-racing-red mb-4">{t('admin.ecuTuning.details.title')}</h2>

              {/* Contact Information */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-2">{t('admin.ecuTuning.details.contact')}</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>{t('admin.ecuTuning.fields.fullName')}:</strong> {selectedRequest.full_name}</p>
                  <p><strong>{t('admin.ecuTuning.fields.email')}:</strong> {selectedRequest.email}</p>
                  <p><strong>{t('admin.ecuTuning.fields.phone')}:</strong> {selectedRequest.phone}</p>
                  {selectedRequest.address && <p><strong>{t('admin.ecuTuning.fields.address')}:</strong> {selectedRequest.address}</p>}
                </div>
              </div>

              {/* Motorcycle Information */}
              <div className="mb-6 border-t pt-4">
                <h3 className="font-bold text-gray-900 mb-2">{t('admin.ecuTuning.details.motorcycle')}</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>{t('admin.ecuTuning.fields.manufacturer')}:</strong> {selectedRequest.manufacturer}</p>
                  <p><strong>{t('admin.ecuTuning.fields.model')}:</strong> {selectedRequest.model}</p>
                  <p><strong>{t('admin.ecuTuning.fields.year')}:</strong> {selectedRequest.year_of_production}</p>
                  {selectedRequest.displacement && <p><strong>{t('admin.ecuTuning.fields.displacement')}:</strong> {selectedRequest.displacement} cc</p>}
                  {selectedRequest.vin_number && <p><strong>{t('admin.ecuTuning.fields.vin')}:</strong> {selectedRequest.vin_number}</p>}
                  {selectedRequest.country_of_origin && <p><strong>{t('admin.ecuTuning.fields.country')}:</strong> {selectedRequest.country_of_origin}</p>}
                  <p><strong>{t('admin.ecuTuning.fields.powerLimiter')}:</strong> {selectedRequest.power_limiter ? t('admin.ecuTuning.fields.yes') : t('admin.ecuTuning.fields.no')}</p>
                </div>
              </div>

              {/* Mechanical Changes */}
              {(selectedRequest.air_intake || selectedRequest.exhaust_system || selectedRequest.other_modifications) && (
                <div className="mb-6 border-t pt-4">
                  <h3 className="font-bold text-gray-900 mb-2">{t('admin.ecuTuning.details.mechanical')}</h3>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p><strong>{t('admin.ecuTuning.fields.airIntake')}:</strong> {selectedRequest.air_intake}</p>
                    <p><strong>{t('admin.ecuTuning.fields.exhaustSystem')}:</strong> {selectedRequest.exhaust_system}</p>
                    {selectedRequest.other_modifications && (
                      <p><strong>{t('admin.ecuTuning.fields.otherModifications')}:</strong> {selectedRequest.other_modifications}</p>
                    )}
                  </div>
                </div>
              )}

              {/* ECU Work Scope */}
              {(selectedRequest.unlocking || selectedRequest.power_limiter_block || selectedRequest.immobilizer_removal || 
                selectedRequest.stage_1 || selectedRequest.stage_2 || selectedRequest.dtc_removal) && (
                <div className="mb-6 border-t pt-4">
                  <h3 className="font-bold text-gray-900 mb-2">{t('admin.ecuTuning.details.ecuWork')}</h3>
                  <div className="space-y-1 text-sm text-gray-700">
                    {selectedRequest.unlocking && <p>✓ {t('admin.ecuTuning.fields.unlocking')}</p>}
                    {selectedRequest.power_limiter_block && <p>✓ {t('admin.ecuTuning.fields.powerLimiterBlock')}</p>}
                    {selectedRequest.immobilizer_removal && <p>✓ {t('admin.ecuTuning.fields.immobilizerRemoval')}</p>}
                    {selectedRequest.stage_1 && <p>✓ {t('admin.ecuTuning.fields.stage1')}</p>}
                    {selectedRequest.stage_2 && <p>✓ {t('admin.ecuTuning.fields.stage2')}</p>}
                    {selectedRequest.dtc_removal && <p>✓ {t('admin.ecuTuning.fields.dtcRemoval')}</p>}
                  </div>
                </div>
              )}

              {/* Payment Method (from ECU form checkboxes) */}
              {(selectedRequest.payment_transfer || selectedRequest.payment_cash) && (
                <div className="mb-6 border-t pt-4">
                  <h3 className="font-bold text-gray-900 mb-2">{t('home.ecuForm.paymentMethod')}</h3>
                  <div className="space-y-1 text-sm text-gray-700">
                    {selectedRequest.payment_transfer && <p>✓ {t('home.ecuForm.paymentTransfer')}</p>}
                    {selectedRequest.payment_cash && <p>✓ {t('home.ecuForm.paymentCash')}</p>}
                  </div>
                </div>
              )}

              {/* Status Management */}
              <div className="border-t pt-4 mt-6">
                <h3 className="font-bold text-gray-900 mb-3">{t('admin.ecuTuning.details.updateStatus')}</h3>
                <div className="space-y-2">
                  {['new', 'reviewed', 'paid', 'completed', 'rejected'].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateRequestStatus(selectedRequest.id, status)}
                      disabled={selectedRequest.status === status}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition ${
                        selectedRequest.status === status
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {t('admin.ecuTuning.details.markAs')} {t(`admin.ecuTuning.filters.${status}`)}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setSelectedRequest(null)}
                className="w-full mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg"
              >
                {t('admin.ecuTuning.details.close')}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEcuTuning;
